import React, { useContext, useState, useMemo } from 'react';
import { AppContext } from '../context/AppContext';
import { User, Role } from '../types';
import Card from '../components/common/Card';
import { PlusIcon, TrashIcon, PencilIcon, MagnifyingGlassIcon, UserCircleIcon, XMarkIcon, ListBulletIcon, Squares2X2Icon } from '@heroicons/react/24/outline';


const EditUserModal: React.FC<{
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (user: User) => void;
}> = ({ user, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState<User | null>(null);

  React.useEffect(() => {
    setFormData(user);
  }, [user]);

  if (!isOpen || !formData) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 w-full max-w-lg relative animate-in fade-in zoom-in duration-200">
        <button onClick={onClose} className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1.5 rounded-lg transition-colors">
          <XMarkIcon className="h-6 w-6" />
        </button>
        <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
          <PencilIcon className="h-7 w-7 mr-2 text-brand-primary" />
          Editar Usuario
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Nombre Completo</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Ej: Juan Pérez" className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-primary focus:border-brand-primary p-3 border placeholder-gray-400 bg-gray-50 hover:bg-white transition-colors" required />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Correo Electrónico</label>
            <input type="email" name="email" value={formData.email || ''} onChange={handleChange} placeholder="ejemplo@empresa.com" className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-primary focus:border-brand-primary p-3 border placeholder-gray-400 bg-gray-50 hover:bg-white transition-colors" required />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Nivel de Permisos</label>
            <select name="role" value={formData.role} onChange={handleChange} className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-primary focus:border-brand-primary p-3 border bg-white transition-colors">
              <option value={Role.OPERADOR}>Operador (Monitoreo Básico)</option>
              <option value={Role.SUPERVISOR}>Supervisor (Edición / Validaciones)</option>
              <option value={Role.ADMIN}>Administrador (Control Total)</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-100">
            <button type="button" onClick={onClose} className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-medium transition-colors">Cancelar</button>
            <button type="submit" className="px-5 py-2.5 bg-brand-primary text-white rounded-xl hover:bg-brand-secondary font-medium shadow-md transition-all">Guardar Cambios</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AdminUsers: React.FC = () => {
  const { users, addUser, updateUser, deleteUser } = useContext(AppContext);
  const [newUser, setNewUser] = useState<Omit<User, 'id'>>({ name: '', role: Role.SUPERVISOR, email: '', avatar: '' });
  const [newPassword, setNewPassword] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewUser(prev => ({ ...prev, [name]: value }));
  };

  const handleAddUser = (e: React.FormEvent) => {
    e.preventDefault();
    if (newUser.name && newUser.email && newPassword) {
      const generatedAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(newUser.name)}&background=random&color=fff&size=128`;
      addUser({
        ...newUser,
        avatar: generatedAvatar
      }, newPassword);
      setNewUser({ name: '', role: Role.SUPERVISOR, email: '', avatar: '' });
      setNewPassword('');
      setIsModalOpen(false);
    }
  };

  const handleOpenEditModal = (user: User) => {
    setEditingUser(user);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditingUser(null);
    setIsEditModalOpen(false);
  };

  const handleSaveChanges = (updatedUser: User) => {
    updateUser(updatedUser);
    handleCloseEditModal();
  };

  const filteredUsers = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return users;
    return users.filter(u =>
      u.name.toLowerCase().includes(term) ||
      (u.email && u.email.toLowerCase().includes(term)) ||
      u.role.toLowerCase().includes(term)
    );
  }, [users, searchTerm]);

  return (
    <div className="w-full space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Gestión de Personal</h1>
          <p className="text-gray-500 mt-1">Administra los accesos, roles y credenciales de los usuarios del sistema.</p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="w-full sm:w-auto flex justify-center items-center py-2.5 px-5 border border-transparent rounded-xl shadow-md text-sm font-bold text-white bg-brand-primary hover:bg-brand-secondary hover:shadow-lg transform active:scale-[0.98] transition-all"
        >
          <PlusIcon className="h-5 w-5 mr-2 stroke-2" />
          Nuevo Usuario
        </button>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white p-3 rounded-2xl shadow-sm border border-gray-100">
        <div className="flex items-center w-full sm:w-96 bg-gray-50 rounded-xl px-3 border border-transparent focus-within:ring-2 focus-within:ring-brand-primary/20 focus-within:border-brand-primary transition-all">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nombre, correo o rol..."
            className="w-full bg-transparent border-none focus:ring-0 p-2.5 text-gray-700 placeholder-gray-400 text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="hidden sm:flex bg-gray-100 p-1 rounded-xl">
          <button
            onClick={() => setViewMode('table')}
            className={`p-2 rounded-lg transition-all ${viewMode === 'table' ? 'bg-white shadow-sm text-brand-primary' : 'text-gray-500 hover:text-gray-700'}`}
            title="Vista de Tabla"
          >
            <ListBulletIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => setViewMode('cards')}
            className={`p-2 rounded-lg transition-all ${viewMode === 'cards' ? 'bg-white shadow-sm text-brand-primary' : 'text-gray-500 hover:text-gray-700'}`}
            title="Vista de Tarjetas"
          >
            <Squares2X2Icon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* MOBILE ALL-TIME CARD VIEW */}
      <div className="sm:hidden grid grid-cols-1 gap-4">
        {filteredUsers.length === 0 ? (
          <div className="text-center py-12 px-4 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
            <p className="text-gray-500 font-medium text-lg mb-1">No se encontraron usuarios</p>
            <p className="text-gray-400 text-sm">Prueba ajustando los filtros de búsqueda.</p>
          </div>
        ) : (
          filteredUsers.map(user => (
            <Card key={user.id} className="hover:shadow-lg transition-all border border-gray-100 hover:border-brand-primary/20 relative group overflow-hidden">
              <div className="absolute top-4 right-4 flex space-x-1 bg-white/90 p-1 rounded-lg border border-gray-100 shadow-sm z-10 transition-all duration-200">
                <button onClick={() => handleOpenEditModal(user)} className="p-1.5 text-gray-500 hover:text-brand-primary hover:bg-amber-50 rounded-md transition-colors" title="Editar">
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button onClick={() => deleteUser(user.id)} className="p-1.5 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors" title="Eliminar">
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>

              <div className="flex items-center space-x-4 mb-4">
                <div className="h-16 w-16 rounded-full shadow-sm overflow-hidden ring-2 ring-gray-100 flex-shrink-0">
                  {user.avatar ? (
                    <img src={user.avatar} alt={user.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full bg-brand-primary/10 flex items-center justify-center">
                      <UserCircleIcon className="h-8 w-8 text-brand-primary" />
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 leading-tight">{user.name}</h3>
                  <p className="text-xs text-gray-500">{user.email || 'Sin correo'}</p>
                </div>
              </div>



              <div className="w-full flex justify-between items-center pt-3 border-t border-gray-100">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold ${user.role === Role.ADMIN ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                  {user.role}
                </span>
                <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider flex items-center">
                  <div className="w-2 h-2 rounded-full bg-green-500 mr-1.5 animate-pulse"></div>
                  Activo
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* DESKTOP VIEW */}
      <div className="hidden sm:block">
        {viewMode === 'table' ? (
          <Card className="overflow-hidden p-0 shadow-sm border border-gray-100">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-3 py-3 sm:px-6 sm:py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Perfil del Usuario</th>
                    <th scope="col" className="px-3 py-3 sm:px-6 sm:py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Seguridad</th>
                    <th scope="col" className="px-3 py-3 sm:px-6 sm:py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Rol de Acceso</th>
                    <th scope="col" className="px-3 py-3 sm:px-6 sm:py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-12 text-center">
                        <p className="text-gray-500 font-medium text-lg mb-1">No se encontraron usuarios</p>
                        <p className="text-gray-400 text-sm">Prueba ajustando los filtros de búsqueda.</p>
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map(user => (
                      <tr key={user.id} className="hover:bg-amber-50/50 transition-colors">
                        <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              {user.avatar ? (
                                <img src={user.avatar} alt={user.name} className="h-10 w-10 rounded-full shadow-sm object-cover" />
                              ) : (
                                <div className="h-10 w-10 bg-brand-primary/10 rounded-full flex items-center justify-center">
                                  <UserCircleIcon className="h-6 w-6 text-brand-primary" />
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-bold text-gray-900">{user.name}</div>
                              <div className="text-xs text-gray-500 mb-1">{user.email || 'Sin correo registrado'}</div>

                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap">
                          <div className="text-xs text-brand-primary font-medium bg-brand-primary/10 inline-flex px-2 py-1 rounded-md">Contraseña Configurada</div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${user.role === Role.ADMIN ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button onClick={() => handleOpenEditModal(user)} className="p-2 text-gray-400 hover:text-brand-primary hover:bg-amber-50 rounded-lg transition-colors mr-1" title="Editar Usuario">
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button onClick={() => deleteUser(user.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar Usuario">
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredUsers.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-500 font-medium text-lg mb-1">No se encontraron usuarios</p>
                <p className="text-gray-400 text-sm">Prueba ajustando los filtros de búsqueda.</p>
              </div>
            ) : (
              filteredUsers.map(user => (
                <Card key={user.id} className="hover:shadow-lg transition-all border border-gray-100 hover:border-brand-primary/20 relative group">
                  <div className="absolute top-4 right-4 flex space-x-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all bg-white/90 backdrop-blur-sm p-1 rounded-lg border border-gray-100 shadow-sm z-10">
                    <button onClick={() => handleOpenEditModal(user)} className="p-1.5 text-gray-500 hover:text-brand-primary hover:bg-amber-50 rounded-md transition-colors" title="Editar">
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button onClick={() => deleteUser(user.id)} className="p-1.5 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors" title="Eliminar">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="flex flex-col items-center text-center">
                    <div className="h-20 w-20 mb-4 rounded-full shadow-md overflow-hidden ring-4 ring-gray-50 pt-0">
                      {user.avatar ? (
                        <img src={user.avatar} alt={user.name} className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full bg-brand-primary/10 flex items-center justify-center">
                          <UserCircleIcon className="h-10 w-10 text-brand-primary" />
                        </div>
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">{user.name}</h3>
                    <p className="text-sm text-gray-500 mb-2">{user.email || 'Sin correo'}</p>


                    <div className="w-full flex justify-between items-center pt-4 border-t border-gray-100 mt-2">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${user.role === Role.ADMIN ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                        {user.role}
                      </span>
                      <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider flex items-center">
                        <div className="w-2 h-2 rounded-full bg-green-500 mr-1.5 animate-pulse"></div>
                        Activo
                      </div>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}
      </div>

      {/* Modal Creador de Usuarios */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 w-full max-w-lg relative animate-in fade-in zoom-in duration-200">
            <button onClick={() => setIsModalOpen(false)} className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1.5 rounded-lg transition-colors">
              <XMarkIcon className="h-6 w-6" />
            </button>
            <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
              <PlusIcon className="h-7 w-7 mr-2 text-brand-primary" />
              Registrar Empleado
            </h2>
            <form onSubmit={handleAddUser} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Nombre Completo</label>
                <input type="text" name="name" value={newUser.name} onChange={handleInputChange} placeholder="Ej: Juan Pérez" className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-primary focus:border-brand-primary p-3 border placeholder-gray-400 bg-gray-50 hover:bg-white transition-colors" required />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Correo Electrónico</label>
                <input type="email" name="email" value={newUser.email || ''} onChange={handleInputChange} placeholder="ejemplo@empresa.com" className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-primary focus:border-brand-primary p-3 border placeholder-gray-400 bg-gray-50 hover:bg-white transition-colors" required />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Contraseña de Acceso</label>
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••••" className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-primary focus:border-brand-primary p-3 border placeholder-gray-400 bg-gray-50 hover:bg-white transition-colors" required />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Nivel de Permisos</label>
                <select name="role" value={newUser.role} onChange={handleInputChange} className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-primary focus:border-brand-primary p-3 border bg-white transition-colors">
                  <option value={Role.OPERADOR}>Operador (Monitoreo Básico)</option>
                  <option value={Role.SUPERVISOR}>Supervisor (Edición / Validaciones)</option>
                  <option value={Role.ADMIN}>Administrador (Control Total)</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-100">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-medium transition-colors">Cancelar</button>
                <button type="submit" className="px-5 py-2.5 bg-brand-primary text-white rounded-xl hover:bg-brand-secondary font-medium shadow-md transition-all">Crear Cuenta</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Editor de Usuarios */}
      <EditUserModal
        isOpen={isEditModalOpen}
        user={editingUser}
        onClose={handleCloseEditModal}
        onSave={handleSaveChanges}
      />
    </div>
  );
};

export default AdminUsers;
